import { useState, useCallback, type ReactNode } from 'react';

/**
 * 🍞 نظام Toasts أنيق (بديل window.alert)
 *
 * الاستخدام:
 *   const { toasts, show, dismiss, ToastContainer } = useToasts();
 *   show({ kind: 'warn', title: 'تفوّق عليك مزايد', body: '...' });
 *   <ToastContainer />
 */

export type ToastKind = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  body?: string;
  duration?: number;
}

const KIND_STYLES: Record<ToastKind, { bg: string; border: string; icon: string; iconBg: string }> = {
  info: {
    bg: 'bg-gradient-to-br from-[#1a3151] to-[#142a47]',
    border: 'border-blue-400/40',
    icon: 'ℹ️',
    iconBg: 'bg-blue-500/20 text-blue-200',
  },
  success: {
    bg: 'bg-gradient-to-br from-[#0f3a2a] to-[#082619]',
    border: 'border-emerald-400/40',
    icon: '✓',
    iconBg: 'bg-emerald-500/20 text-emerald-300',
  },
  warn: {
    bg: 'bg-gradient-to-br from-[#3d2a08] to-[#2a1c00]',
    border: 'border-amber-400/40',
    icon: '⚠️',
    iconBg: 'bg-amber-500/20 text-amber-300',
  },
  error: {
    bg: 'bg-gradient-to-br from-[#3a1010] to-[#260808]',
    border: 'border-red-400/40',
    icon: '✕',
    iconBg: 'bg-red-500/20 text-red-300',
  },
};

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (opts: { kind?: ToastKind; title: string; body?: string; duration?: number }) => {
      const id = Math.random().toString(36).slice(2);
      const toast: Toast = {
        id,
        kind: opts.kind ?? 'info',
        title: opts.title,
        body: opts.body,
        duration: opts.duration ?? 4500,
      };
      setToasts((cur) => [...cur, toast]);
      if (toast.duration && toast.duration > 0) {
        setTimeout(() => dismiss(id), toast.duration);
      }
      return id;
    },
    [dismiss],
  );

  const ToastContainer = useCallback(
    ({ position = 'top-center' }: { position?: 'top-center' | 'top-right' | 'bottom-right' }) => {
      const posClass =
        position === 'top-center'   ? 'top-6 left-1/2 -translate-x-1/2 items-center' :
        position === 'top-right'    ? 'top-6 right-6 items-end' :
        /* bottom-right */            'bottom-6 right-6 items-end';
      return (
        <div className={`fixed z-[60] flex flex-col gap-2 pointer-events-none ${posClass}`} dir="rtl">
          {toasts.map((t) => {
            const s = KIND_STYLES[t.kind];
            return (
              <div
                key={t.id}
                className={`pointer-events-auto min-w-[280px] max-w-md rounded-2xl border-2 ${s.border} ${s.bg} shadow-2xl backdrop-blur-md text-white animate-toast-in`}
              >
                <div className="flex items-start gap-3 p-4">
                  <div className={`shrink-0 w-9 h-9 rounded-xl ${s.iconBg} flex items-center justify-center text-base font-black`}>
                    {s.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm md:text-base leading-tight">{t.title}</div>
                    {t.body && <div className="text-xs md:text-sm text-white/70 mt-1 leading-relaxed">{t.body}</div>}
                  </div>
                  <button
                    onClick={() => dismiss(t.id)}
                    className="shrink-0 text-white/40 hover:text-white text-lg leading-none w-6 h-6 flex items-center justify-center"
                    aria-label="إغلاق"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [toasts, dismiss],
  );

  return { toasts, show, dismiss, ToastContainer };
}

/* ✨ تأكد من إضافة الـ animation الخاص بدخول الـ toast في index.css:
   @keyframes toast-in { from { opacity: 0; transform: translateY(-12px) scale(0.96); } to { opacity: 1; transform: none; } }
   .animate-toast-in { animation: toast-in 0.25s ease-out both; }
*/
export function ToastProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
